/**
 * Armazenamento de JWT em sessionStorage com cifragem leve via Web
 * Crypto. A chave AES-GCM e gerada por sessao e guardada num bucket
 * paralelo -- reduz o blast radius de um XSS basico (ladrao precisa
 * pegar tanto o cipher quanto a key).
 *
 * Limitacoes conhecidas:
 * - CSP estrita no nginx e a defesa principal contra XSS.
 * - v2: migrar para cookie HTTPOnly servido pelo backend.
 */

const STORAGE_KEY = "orbit:token";
const KEY_STORAGE = "orbit:tokenkey";

const subtle = globalThis.crypto?.subtle;

let memoryToken: string | null = null;

async function getOrCreateKey(): Promise<CryptoKey | null> {
  if (!subtle) return null;
  const raw = sessionStorage.getItem(KEY_STORAGE);
  if (raw) {
    const bytes = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
    return subtle.importKey("raw", bytes, "AES-GCM", true, [
      "encrypt",
      "decrypt",
    ]);
  }
  const key = await subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
  const exported = new Uint8Array(await subtle.exportKey("raw", key));
  sessionStorage.setItem(
    KEY_STORAGE,
    btoa(String.fromCharCode(...exported)),
  );
  return key;
}

export async function setToken(token: string): Promise<void> {
  memoryToken = token;
  if (!subtle) {
    sessionStorage.setItem(STORAGE_KEY, token);
    return;
  }
  const key = await getOrCreateKey();
  if (!key) {
    sessionStorage.setItem(STORAGE_KEY, token);
    return;
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = await subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(token),
  );
  const ivB64 = btoa(String.fromCharCode(...iv));
  const dataB64 = btoa(String.fromCharCode(...new Uint8Array(enc)));
  sessionStorage.setItem(STORAGE_KEY, `${ivB64}:${dataB64}`);
}

export async function getToken(): Promise<string | null> {
  if (memoryToken) return memoryToken;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  if (!subtle || !raw.includes(":")) {
    memoryToken = raw;
    return raw;
  }
  const [ivB64, dataB64] = raw.split(":");
  if (!ivB64 || !dataB64) return null;
  const key = await getOrCreateKey();
  if (!key) return null;
  try {
    const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
    const data = Uint8Array.from(atob(dataB64), (c) => c.charCodeAt(0));
    const dec = await subtle.decrypt({ name: "AES-GCM", iv }, key, data);
    const token = new TextDecoder().decode(dec);
    memoryToken = token;
    return token;
  } catch {
    return null;
  }
}

export function clearToken(): void {
  memoryToken = null;
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(KEY_STORAGE);
}

export const tokenStorage = { getToken, setToken, clearToken };
